import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowRight, Sparkle } from "@phosphor-icons/react";

const gbp = (n) => `£${Number(n || 0).toLocaleString("en-GB")}`;

export function PriceCalculator({ className = "" }) {
  const [cfg, setCfg] = useState(null);
  const [n, setN] = useState(1);

  useEffect(() => {
    api.get("/billing/plans").then(({ data }) => setCfg(data)).catch(() => {});
  }, []);

  const result = useMemo(() => {
    if (!cfg) return null;
    const base = cfg.base_per_company;
    const threshold = cfg.enterprise_threshold;
    const paid = (cfg.plans || []).filter((p) => !p.enterprise);
    const enterprise = n >= threshold;
    const plan = enterprise ? null : paid.find((p) => p.companies >= n) || null;
    const listMonthly = n * base;
    const save = plan ? Math.max(0, listMonthly - plan.monthly) : 0;
    const savePct = plan && listMonthly > 0 ? Math.round((save / listMonthly) * 100) : 0;
    return { base, threshold, enterprise, plan, listMonthly, save, savePct };
  }, [cfg, n]);

  if (!cfg || !result) return <div className={`h-56 ${className}`} data-testid="price-calc-loading" />;
  const { base, threshold, enterprise, plan, listMonthly, save, savePct } = result;
  const sliderMax = threshold; // threshold and above => Enterprise

  return (
    <div className={className} data-testid="price-calculator">
      <div className="flex items-center gap-2 mb-1">
        <Sparkle size={16} weight="fill" style={{ color: "#F37A24" }} />
        <span className="overline text-navy/70">Instant quote</span>
      </div>
      <label htmlFor="price-calc-slider" className="block font-heading font-bold text-navy text-lg">
        How many companies do you manage?
      </label>

      <div className="flex items-end justify-between mt-4 mb-2">
        <div className="font-heading font-black text-navy text-4xl tabular" data-testid="price-calc-companies">
          {enterprise ? `${threshold}+` : n}
        </div>
        <div className="text-navy/65 text-xs">Drag to see your price</div>
      </div>

      <input
        id="price-calc-slider"
        data-testid="price-calc-slider"
        type="range"
        min={1}
        max={sliderMax}
        value={n}
        onChange={(e) => setN(Number(e.target.value))}
        className="w-full accent-[#F37A24] cursor-pointer"
        aria-label="Number of companies you manage"
      />
      <div className="flex justify-between text-[0.7rem] text-navy/65 mt-1">
        <span>1</span>
        <span>{threshold}+</span>
      </div>

      {enterprise ? (
        <div className="mt-5 rounded-xl p-5 text-center" style={{ background: "rgba(14,15,63,0.05)" }} data-testid="price-calc-enterprise">
          <div className="font-heading font-bold text-navy text-lg">Enterprise</div>
          <p className="text-navy/70 text-sm mt-1">
            Managing {threshold}+ companies? We'll build a custom plan around your practice.
          </p>
          <a href="mailto:hello@workwithlottie.com?subject=Enterprise%20enquiry" data-testid="price-calc-enterprise-cta"
            className="mt-4 inline-flex items-center gap-2 font-semibold px-6 py-3 rounded-xl border border-navy text-navy hover:bg-navy hover:text-white transition-colors">
            Contact Sales
          </a>
        </div>
      ) : (
        <div className="mt-5 rounded-xl p-5" style={{ background: "rgba(244,122,36,0.07)" }}>
          <div className="flex items-baseline justify-between">
            <div>
              <div className="text-navy/70 text-xs">Recommended plan</div>
              <div className="font-heading font-bold text-navy text-xl" data-testid="price-calc-plan">{plan.name}</div>
              <div className="text-navy/65 text-xs mt-0.5">Covers up to {plan.companies} {plan.companies === 1 ? "company" : "companies"}</div>
            </div>
            <div className="text-right">
              <div className="font-heading font-black text-navy text-3xl" data-testid="price-calc-price">{gbp(plan.monthly)}</div>
              <div className="text-navy/70 text-xs">/month · excl. VAT</div>
            </div>
          </div>

          {savePct > 0 ? (
            <div className="mt-4 pt-4 border-t border-[#F37A24]/20">
              <div className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full mb-2"
                style={{ background: "rgba(0,168,89,0.12)", color: "#00A859" }} data-testid="price-calc-save-badge">
                You save {gbp(save)}/mo — {savePct}% off
              </div>
              <div className="text-navy/70 text-xs leading-snug" data-testid="price-calc-working">
                {n} {n === 1 ? "company" : "companies"} × {gbp(base)} = {gbp(listMonthly)}/mo at the per-company rate.
                With {plan.name} you pay just {gbp(plan.monthly)}/mo.
              </div>
            </div>
          ) : (
            <div className="mt-4 pt-4 border-t border-[#F37A24]/20 text-navy/70 text-xs" data-testid="price-calc-working">
              Our per-company base rate — {gbp(base)}/mo for a single company.
            </div>
          )}

          <Link to={`/start?plan=${plan.id}`} data-testid="price-calc-cta"
            className="mt-4 w-full inline-flex items-center justify-center gap-2 text-white font-semibold py-3 rounded-xl transition-transform hover:scale-[1.01]"
            style={{ background: "#F37A24" }}>
            Start free trial <ArrowRight size={17} weight="bold" />
          </Link>
        </div>
      )}
    </div>
  );
}
